import { Archive } from "lucide-react";
import { SessionCard } from "./SessionCard";
import { SectionHeader } from "./section-header";
import { TabButton } from "./tab-button";
import { PaginationFooter } from "../../../components/ui/pagination-footer";
import { usePagination } from "../../../hooks/use-pagination";
import { PAGE_SIZE_DEFAULT } from "../../../lib/pagination";
import {
  canViewOtherVotesForUser,
  getVoteIdentityDisplayMode,
} from "../../../lib/vote-visibility";

// Past sessions are closed (finalized/archived/cancelled). For finalized or
// archived sessions identities must reveal post-finalize (R9: otherwise
// anonymized aliases stay permanently hidden once a session lands here). We
// derive isFinalized from status; cancelled sessions with restrictive
// visibility stay hidden (their votes never finalized), which is correct.
function resolvePastSessionIdentityReveal(scheduler, currentUserId) {
  if (!scheduler) return false;
  return canViewOtherVotesForUser({
    voteVisibility: scheduler.voteVisibility,
    isCreator:
      String(scheduler.creatorId || "").trim() ===
      String(currentUserId || "").trim(),
    hasVoted: (scheduler.voters || []).some((voter) => voter?.id === currentUserId),
    allParticipantsVoted: scheduler.votesAllSubmitted === true,
    isFinalized: scheduler.status === "FINALIZED",
  });
}

function resolveEmptyMessage(tab) {
  if (tab === "cancelled") return "No cancelled sessions yet.";
  if (tab === "archived") return "No archived polls. Use the archive action on any session card.";
  return "No past sessions yet.";
}

export function PastSessionsSection({
  pastSessionsTab,
  onTabChange,
  pastFinalized = [],
  cancelledSessions = [],
  archivedSessions = [],
  getGroupColor,
  groupsById = {},
  currentUserId = null,
  currentUser = null,
  viewer = {},
  archivedSchedulerIdSet = new Set(),
  sessionArchiveBusy = {},
  onToggleSessionArchive,
  onEditSession,
  onDeleteSession,
  // Resets the pager to page 1 when it changes. The dashboard composes its
  // filter/search signature with the active tab so changing EITHER returns the
  // viewer to page 1; falls back to the tab alone when used standalone.
  resetKey = pastSessionsTab,
}) {
  const activeList =
    pastSessionsTab === "cancelled"
      ? cancelledSessions
      : pastSessionsTab === "archived"
        ? archivedSessions
        : pastFinalized;

  const pager = usePagination(activeList, {
    pageSize: PAGE_SIZE_DEFAULT,
    resetKey,
  });

  const renderSessionCard = (scheduler) => {
    const isArchived =
      pastSessionsTab === "archived" ? true : archivedSchedulerIdSet.has(scheduler.id);
    return (
      <SessionCard
        key={scheduler.id}
        scheduler={scheduler}
        canManage={Boolean(scheduler.creatorId && currentUserId && scheduler.creatorId === currentUserId)}
        isArchived={isArchived}
        archiveBusy={Boolean(sessionArchiveBusy[scheduler.id])}
        onArchiveToggle={onToggleSessionArchive ? () => onToggleSessionArchive(scheduler) : undefined}
        onEditPoll={onEditSession ? () => onEditSession(scheduler) : undefined}
        onDeletePoll={onDeleteSession ? () => onDeleteSession(scheduler) : undefined}
        winningSlot={scheduler.winningSlot}
        slots={scheduler.slots}
        displayTimeZone={scheduler.displayTimeZone}
        showTimeZone={scheduler.showTimeZone}
        groupColor={
          scheduler.questingGroupId ? getGroupColor(scheduler.questingGroupId) : null
        }
        attendanceSummary={scheduler.attendanceSummary}
        participants={scheduler.effectiveParticipants || []}
        voters={scheduler.voters || []}
        submittedVoteCount={scheduler.submittedVoteCount}
        totalParticipantCount={scheduler.totalParticipantCount}
        voteIdentityDisplayMode={getVoteIdentityDisplayMode({
          isCreator:
            String(scheduler?.creatorId || "").trim() ===
            String(currentUserId || "").trim(),
          hideVoterIdentities: scheduler?.hideVoterIdentities,
          voteAnonymization: scheduler?.voteAnonymization,
          detailsRevealed: resolvePastSessionIdentityReveal(scheduler, currentUserId),
        })}
        currentUser={currentUser}
        viewer={viewer}
        questingGroup={
          scheduler.questingGroupId ? groupsById[scheduler.questingGroupId] : null
        }
      />
    );
  };

  return (
    <section className="rounded-3xl bg-white p-6 shadow-xl shadow-slate-200 dark:bg-slate-800 dark:shadow-slate-900/50">
      <SectionHeader title="Past Sessions" subtitle="Finalized, cancelled, and archived" />

      <div className="mt-3 flex w-fit max-w-full gap-2 overflow-x-auto rounded-full border border-slate-200 bg-slate-50 p-1 dark:border-slate-600 dark:bg-slate-700">
        <TabButton
          active={pastSessionsTab === "finalized"}
          onClick={() => onTabChange("finalized")}
        >
          Finalized
        </TabButton>
        <TabButton
          active={pastSessionsTab === "cancelled"}
          onClick={() => onTabChange("cancelled")}
        >
          Cancelled ({cancelledSessions.length})
        </TabButton>
        <TabButton
          active={pastSessionsTab === "archived"}
          onClick={() => onTabChange("archived")}
          data-testid="past-sessions-archived-tab"
        >
          <span className="flex items-center gap-1">
            <Archive className="h-3 w-3" />
            Archived ({archivedSessions.length})
          </span>
        </TabButton>
      </div>

      <div className="mt-4 space-y-2">
        {activeList.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {resolveEmptyMessage(pastSessionsTab)}
          </p>
        ) : (
          pager.pageItems.map(renderSessionCard)
        )}
        <PaginationFooter
          page={pager.page}
          pageCount={pager.pageCount}
          rangeStart={pager.rangeStart}
          rangeEnd={pager.rangeEnd}
          total={pager.total}
          hasPrev={pager.hasPrev}
          hasNext={pager.hasNext}
          onPrev={pager.goPrev}
          onNext={pager.goNext}
          label="Past sessions"
          itemNoun="session"
        />
      </div>
    </section>
  );
}
